<template>
    <div id="future-prescriptions-queue-container">
        <card title="Claims Pending to be Reversed">
            <div>
                <b-row>
                    <b-col>
                        <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" :prepopulate="true"
                            :clearable="true" @input="search" />
                    </b-col>
                </b-row>
                <b-row v-show="!loading">
                    <b-col>
                        <checkbox v-model="showVoid">Show Void & Expired ({{ voidCount }})</checkbox>
                    </b-col>
                </b-row>
            </div>

            <PrescriptionsListComponent v-show="!loading" :prescriptions="pagedPrescriptions"
                :showGeneralActionButtons="false" :showSelectCheckox="false" :action-buttons="['edit']"
                :showVoidBox="false" :always-show-void="true"
                :manual-sort="['fillDate', 'productNameShort', 'programName']" :default-sort="sortBy"
                @sort-changed="handleSort" />

            <BottomPagerBar v-model="pageNumber" item-name="claim" :total-rows="filteredPrescriptions.length" show-total
                :per-page="pageSize" show-page-size-selector @change-page-size="pageSize = $event" :loading="loading" />
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Watch, Vue } from 'vue-property-decorator';
import Axios from 'axios';
import { DatePicker } from "element-ui";
import { NotificationOptions } from "@/util/NotificationOptionsPresets"

import { Prescription } from '@/models/Prescription';
import { Store } from '@/models/Store';

import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';
import { saveQueueFilters, loadQueueFilters } from '@/util/queueFilterPersistence';

@Component({
    name: "ReversalQueuePage",
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        BottomPagerBar,
        Checkbox
    },
})
export default class ReversalQueuePage extends Vue {
    private prescriptions: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private loading: boolean = false;
    private showVoid: boolean = false;

    protected pageNumber: number = 1;
    protected pageSize: number = 100;
    private sortBy: string = "fillDate";
    private sortDirection: string = "D";

    private skipFilterSave = false;

    @Watch("storeFilter")
    @Watch("showVoid")
    resetPage() {
        this.persistFilters();
        this.pageNumber = 1;
    }

    created() {
        this.pageSize = getPageSizePreference(this, 'pageSize_ReversalQueue', 100);
        this.restoreFilters();
        this.search();
    }

    @Watch("pageSize")
    onPageSizeChanged() {
        savePageSizePreference(this, 'pageSize_ReversalQueue', this.pageSize);
    }

    private restoreFilters() {
        const saved = loadQueueFilters(this, 'reversalQueue_filters');
        if (!saved) return;
        this.skipFilterSave = true;
        if (saved.store?.id) this.storeFilter = new Store(saved.store);
        if (typeof saved.showVoid === 'boolean') this.showVoid = saved.showVoid;
        this.$nextTick(() => { this.skipFilterSave = false; });
    }

    private persistFilters() {
        if (this.skipFilterSave) return;
        saveQueueFilters(this, 'reversalQueue_filters', {
            store: this.storeFilter?.id ? { id: this.storeFilter.id, name: this.storeFilter.name } : null,
            showVoid: this.showVoid,
        });
    }

    async search() {
        try {
            this.loading = true;
            const response = await Axios.get<Prescription[]>(`/Prescription/get-reversal-queue/${this.storeFilter?.id || ''}`);
            this.prescriptions = response.data.map(rx => new Prescription(rx));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.warn("Error while fetching prescriptions by date", error);
        } finally {
            this.loading = false;
        }
    }

    get sortedPrescriptions() {
        let sorted = [...this.prescriptions];

        switch (this.sortBy) {
            case "productNameShort":
                sorted.sort((a, b) => a.productNameShort?.localeCompare(b.productNameShort));
                break;
            case "programName":
                sorted.sort((a, b) => a.programName?.trim().localeCompare(b.programName?.trim()));
                break;
            default:
                sorted.sort((a, b) => new Date(a.fillDate).getTime() - new Date(b.fillDate).getTime());
                break;
        }

        if (this.sortDirection === "D") sorted.reverse();

        return sorted;
    }

    get filteredPrescriptions() {
        return this.sortedPrescriptions.filter(rx => this.showVoid || (!rx.isVoid && !rx.isExpired));
    }

    get voidCount() {
        return this.prescriptions.filter(rx => (rx.isVoid || rx.isExpired)).length;
    }

    get pagedPrescriptions() {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.filteredPrescriptions.slice(startIndex, endIndex);
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        this.pageNumber = 1;
    }
}

</script>
